import type Phaser from "phaser";
import {
  aimSegmentAlpha,
  buildAimLineFrame,
  type AimPoint,
} from "./aimGeometry.js";
import { DEPTH } from "../objects/depthLayers.js";
import { PALETTE_NUM } from "../themes/palette.js";

const MAX_LEN = 60; // 타겟 거리와 무관하게 쓰는 짧은 고정 조준 가이드 길이
const START_OFFSET = 2; // origin은 이미 포구 tip이므로 우주선에서 떨어져 보이지 않게 최소 간격만 둔다.
const AIM_FADE_MS = 0; // 자동 조준 타겟 변경 때 점멸처럼 보이지 않도록 채움 연출을 끈다.
const SEGMENTS = 1;
const TEXTURE_KEY = "aim_line_gradient_bright";
const TEXTURE_W = 8;
const CORE_COLOR = PALETTE_NUM.aimCore;
const GLOW_COLOR = PALETTE_NUM.aimGlow;
const GLOW_ALPHA = 0.24;
const CORE_ALPHA = 0.68;

/**
 * 에임 직선 가이드 — 발사 원점에서 실제 발사 방향(가장 가까운 벽돌)으로 그라데이션 빛줄기를 그린다.
 * GameScene에서 추출(god-object 분해): fade 상태(연출 비활성 시 즉시 1)·이전 타겟을 본 뷰가 보유하고,
 * 매 프레임 `update(now, origin, target)`로 갱신한다. 각도/페이드/기하는 순수 함수(aimAngle·aimLineFade·
 * aimGeometry)에 위임하고, 본 클래스는 Graphics 렌더만 담당한다(Phaser 의존 — 단위테스트 비용 큼).
 */
export class AimLineView {
  private line?: Phaser.GameObjects.Image;
  private enabled = true;
  /** 채움 연출 시작 시각(scene clock ms). 타겟 변경 시 갱신. */
  private fadeStartMs = 0;
  /** 직전 프레임의 조준 타겟 — 변경 감지로 채움 연출을 리셋한다. */
  private prevTarget: AimPoint | null = null;

  constructor(private readonly scene: Phaser.Scene) {}

  /** Image 생성 (1회). 벽돌·발사체 뒤(depth 0), 배경 위. */
  build(): void {
    ensureAimLineTexture(this.scene);
    this.line = this.scene.add
      .image(0, 0, TEXTURE_KEY)
      .setName("aimLineImage")
      .setOrigin(0.5, 1)
      .setDepth(DEPTH.aimLine)
      .setVisible(false)
      .setActive(false);
  }

  /** 게임뷰 클립 마스크 적용/해제 — 펼치기 시 조준선이 좁아진 게임뷰 밖으로 새지 않게(null=해제). */
  setMask(mask: Phaser.Display.Masks.GeometryMask | null): void {
    if (mask) this.line?.setMask(mask);
    else this.line?.clearMask();
  }

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (!enabled) this.line?.setVisible(false).setActive(false);
  }

  /**
   * 매 프레임 갱신 — origin에서 target 방향으로 빛줄기를 그린다.
   * target(가장 가까운 벽돌)이 바뀌어도 채움 연출은 비활성이라 즉시 전체 길이로 표시된다.
   * 길이는 하이브리드: 타겟이 가까우면 그 벽돌에서 멈추고, 멀거나 없으면 고정 MAX_LEN.
   */
  update(nowMs: number, origin: AimPoint, target: AimPoint | null): void {
    const line = this.line;
    if (!line || !this.enabled) return;
    if (!target) {
      this.prevTarget = null;
      this.fadeStartMs = nowMs;
      line.setVisible(false).setActive(false);
      return;
    }

    const frame = buildAimLineFrame({
      nowMs,
      origin,
      target,
      prevTarget: this.prevTarget,
      fadeStartMs: this.fadeStartMs,
      maxLen: MAX_LEN,
      startOffset: START_OFFSET,
      fadeMs: AIM_FADE_MS,
      segmentCount: SEGMENTS,
    });
    this.fadeStartMs = frame.fadeStartMs;
    this.prevTarget = frame.prevTarget;
    if (frame.segments.length === 0) {
      line.setVisible(false).setActive(false);
      return;
    }

    const first = frame.segments[0]!;
    const last = frame.segments[frame.segments.length - 1]!;
    const dx = last.x1 - first.x0;
    const dy = last.y1 - first.y0;
    const len = Math.hypot(dx, dy);
    if (len <= 0) {
      line.setVisible(false).setActive(false);
      return;
    }
    const angle = Math.atan2(dy, dx);
    line
      .setPosition(first.x0, first.y0)
      .setRotation(angle + Math.PI / 2)
      .setDisplaySize(TEXTURE_W, len)
      .setVisible(true)
      .setActive(true);
  }
}

function ensureAimLineTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(TEXTURE_KEY)) return;
  const texture = scene.textures.createCanvas(TEXTURE_KEY, TEXTURE_W, MAX_LEN);
  if (!texture) throw new Error(`Failed to create texture: ${TEXTURE_KEY}`);
  const ctx = texture.getContext();
  const centerX = TEXTURE_W / 2;
  ctx.clearRect(0, 0, TEXTURE_W, MAX_LEN);

  drawVerticalFadeLine(ctx, centerX, 7, GLOW_COLOR, GLOW_ALPHA);
  drawVerticalFadeLine(ctx, centerX, 2, CORE_COLOR, CORE_ALPHA);
  texture.refresh();
}

function drawVerticalFadeLine(
  ctx: CanvasRenderingContext2D,
  x: number,
  width: number,
  color: number,
  baseAlpha: number,
): void {
  ctx.lineWidth = width;
  ctx.lineCap = "round";
  for (let y = 0; y < MAX_LEN; y++) {
    const t0 = 1 - y / Math.max(1, MAX_LEN - 1);
    const alpha = aimSegmentAlpha(baseAlpha, t0);
    if (alpha <= 0) continue;
    ctx.strokeStyle = rgba(color, alpha);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + 1);
    ctx.stroke();
  }
}

function rgba(color: number, alpha: number): string {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return `rgba(${r},${g},${b},${alpha})`;
}
